/* eslint-disable css-modules/no-unused-class  */
import { FC, useMemo } from 'react';
import classNames from 'classnames';
import { ResponsiveBar } from '@nivo/bar';
import { linearGradientDef } from '@nivo/core';
import { makeStyles } from '../../../uiLibraryImports';
import { BarChartProps, BarData } from './barChartTypes';
import useResizeObserver from './useResizeObserver';
import styles from './barChart.module.scss';

const useStyles = makeStyles(() => ({
    label: {
        fontSize: '11px',
        fontFamily: 'sans-serif',
        textAlign: 'center'
    }
}));

const calcIdsToItems = (data: BarData[]): { [key: string]: BarData } => {
    const idsToItems: { [key: string]: BarData } = {};
    data.forEach(item => {
        idsToItems[item.id] = item;
    });

    return idsToItems;
};

const defaultBottomLegendWidth = 47;
const minBottomLegendWidth = 30;
const chartMarginWidth = 180;

const BarChart: FC<BarChartProps> = (props: BarChartProps) => {
    const { data, gridYValues, axisBottomLegend, axisLeftLegend } = props;
    const classes = useStyles();
    const { width: chartWidth, resizedContainerRef } = useResizeObserver();
    const idsToItems = useMemo(() => calcIdsToItems(data), [data]);
    const hasSubTitles = useMemo(() => data.filter(item => item.subTitle !== undefined).length > 0, [data]);

    const getBottomLegendWidth = (): number => {
        let bottomLegendWidth = defaultBottomLegendWidth;
        const netChartWidth = chartWidth - chartMarginWidth;
        if (netChartWidth > minBottomLegendWidth) {
            const numberOfBars = data.length;
            if (numberOfBars === 0) return bottomLegendWidth;
            bottomLegendWidth = netChartWidth / numberOfBars;
        }

        return bottomLegendWidth;
    };

    const bottomLegendWidth = useMemo(() => getBottomLegendWidth(), [getBottomLegendWidth]);

    const renderTick = ({ value, x, y }: any): JSX.Element => {
        const subTitle = idsToItems[value]?.subTitle;
        let title = '';

        if (idsToItems[value]) {
            title = idsToItems[value].displayName || value;
        }

        return (
            <foreignObject
                x={x - bottomLegendWidth / 2}
                y={y + 5}
                width={bottomLegendWidth}
                height={subTitle ? 60 : 45}
            >
                <div title={title} className={classNames(classes.label, 'tick', 'class-ellipsis-2-lines')}>
                    {title}
                </div>
                {subTitle && (
                    <div
                        title={value}
                        className={classNames(classes.label, 'tick', 'tick-subtitle', 'class-ellipsis-2-lines')}
                    >
                        {subTitle}
                    </div>
                )}
            </foreignObject>
        );
    };

    const keys = ['strong', 'poor', 'fair'];

    const indexBy = 'id';
    const theme = {
        text: {
            fontSize: 27,
            fill: 'rgba(241, 120, 174, 1)',
            outlineWidth: 0,
            outlineColor: 'transparent',
            fontWeight: 700
        },
        grid: {
            line: {
                stroke: 'rgba(255, 255, 255, 0.25)'
            }
        },
        axis: {
            legend: {
                text: {
                    fontSize: 12,
                    fill: '#333333',
                    outlineWidth: 0,
                    outlineColor: 'transparent'
                }
            },
            ticks: {
                line: {
                    stroke: '#ffffff',
                    strokeWidth: 1
                },
                text: {
                    fontSize: 11,
                    fill: '#fdfcfc',
                    outlineWidth: 0,
                    outlineColor: 'transparent'
                }
            }
        },
        legends: {
            title: {
                text: {
                    fontSize: 11,
                    fill: '#333333',
                    outlineWidth: 0,
                    outlineColor: 'transparent'
                }
            },
            text: {
                fontSize: 11,
                fill: '#333333',
                outlineWidth: 0,
                outlineColor: 'transparent'
            },
            ticks: {
                line: {},
                text: {
                    fontSize: 10,
                    fill: '#333333',
                    outlineWidth: 0,
                    outlineColor: 'transparent'
                }
            }
        }
    };
    const TotalLabels = ({ bars, yScale }: { bars: any; yScale: any }) => {
        const labelMargin = 20;

        return bars.map(
            (
                { data: { data, indexValue }, x, width }: { data: { data: any; indexValue: any }; x: any; width: any },
                i: any
            ) => {
                const total = Object.keys(data)
                    .filter(key => key !== indexBy)
                    .reduce((a, key) => a + data[key], 0);

                return (
                    <g transform={`translate(${x}, ${yScale(total) - labelMargin})`} key={`${indexValue}-${i}`}>
                        <text
                            className={styles.label}
                            x={width / 2}
                            y={labelMargin / 2}
                            textAnchor="middle"
                            alignmentBaseline="central"
                        >
                            {`${total}%`}
                        </text>
                    </g>
                );
            }
        );
    };

    return (
        <div ref={resizedContainerRef} className={styles.bar}>
            <ResponsiveBar
                defs={[
                    linearGradientDef('strong', [
                        { offset: 0, color: 'rgba(77, 187, 64, 0)' },
                        { offset: 100, color: '#4DBB40' }
                    ]),
                    linearGradientDef('poor', [
                        { offset: 0, color: 'rgba(255, 94, 123, 0)' },
                        { offset: 100, color: 'rgba(255, 94, 123, 1)' }
                    ]),
                    linearGradientDef('fair', [
                        { offset: 0, color: 'rgba(249, 174, 77, 0)' },
                        { offset: 100, color: 'rgba(249, 174, 77, 1)' }
                    ])
                ]}
                fill={[
                    { match: { id: 'strong' }, id: 'strong' },
                    { match: { id: 'poor' }, id: 'poor' },
                    { match: { id: 'fair' }, id: 'fair' }
                ]}
                keys={keys}
                theme={theme}
                data={data}
                indexBy="id"
                maxValue={100}
                minValue={0}
                labelSkipHeight={26}
                margin={{ top: 100, right: 60, bottom: hasSubTitles ? 100 : 60, left: 60 }}
                padding={0.9}
                isInteractive={false}
                animate={false}
                gridYValues={gridYValues}
                layers={['grid', 'axes', 'bars', TotalLabels, 'markers', 'legends']}
                axisLeft={{
                    tickSize: 0,
                    tickPadding: 15,
                    tickValues: gridYValues,
                    legend: axisLeftLegend,
                    legendOffset: -50,
                    legendPosition: 'middle'
                }}
                axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    renderTick,
                    legend: axisBottomLegend,
                    legendOffset: hasSubTitles ? 80 : 40,
                    legendPosition: 'middle'
                }}
                borderColor={(bar: any): string => {
                    return bar.data.data.isSelected ? '#565360' : bar.data.data.color;
                }}
                borderWidth={3}
                borderRadius={4}
                enableLabel={false}
            />
        </div>
    );
};

export default BarChart;
